import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Box } from '@mui/material';
import '../../styles/styles.css';
import PropertyCard from '../../components/properties/PropertyCard';
import { PropertyListing } from '../../types/Property';

const formatPrice = (price: number, propertyCurrency: string) => {
  let locale = 'es-CR';
  let symbol = '';

  switch (propertyCurrency) {
    case 'CRC':
      locale = 'es-CR';
      symbol = '₡';
      break;
    case 'USD':
      locale = 'en-US';
      symbol = '$';
      break;
    default:
      locale = 'en-US';
      symbol = '$';
  }

  return `${symbol}${price.toLocaleString(locale)}`;
};




const createPriceIcon = (
  price: number,
  showPrice: boolean,
  iconSize: [number, number],
  currency: string = 'CRC'
) => {
  const formattedPrice = showPrice ? formatPrice(price, currency) : '';

  return new L.DivIcon({
    className: 'price-icon',
    html: `
      <div style="display: flex; align-items: center; justify-content: center;">
        <img src="/assets/images/alien.png" alt="Alien icon" style="width: 20px; height: 20px; ${showPrice ? 'margin-right: 5px;' : ''}" />
        ${formattedPrice}
      </div>
    `,
    iconSize: iconSize,
    popupAnchor: [0, -20],
  });
};


interface MapViewProps {
  properties: PropertyListing[];
  onOpenModal: (price: number, currency: string) => void;
  selectedCurrency: string;
  sellRate: number;
  buyRate: number;
}

const generateRandomOffset = () => {
  const minOffset = 0.0027; 
  const maxOffset = 0.0045;
  const offset = minOffset + Math.random() * (maxOffset - minOffset);
  return Math.random() < 0.5 ? -offset : offset;
};


const MapView: React.FC<MapViewProps> = ({ properties, onOpenModal, selectedCurrency, buyRate, sellRate }) => {
  const [iconSize, setIconSize] = useState<[number, number]>([100, 20]);
  const [showPrice, setShowPrice] = useState(true);
  const [userPosition, setUserPosition] = useState<[number, number] | null>(null);
  const [obscuredProperties, setObscuredProperties] = useState<PropertyListing[]>([]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserPosition([latitude, longitude]);
        },
        (error) => {
          console.error("Error obteniendo la ubicación del usuario:", error);
          setUserPosition([9.7489, -83.7534]); 
        }
      );
    } else {
      console.warn("Geolocalización no está soportada por este navegador.");
      setUserPosition([9.7489, -83.7534]);
    }
  }, []);

  useEffect(() => {
    const modifiedProperties = properties.map((property) => ({
      ...property,
      latitude: (parseFloat(property.latitude) + generateRandomOffset()).toFixed(6),
      longitude: (parseFloat(property.longitude) + generateRandomOffset()).toFixed(6),
    }));

    setObscuredProperties(modifiedProperties);
  }, [properties]);

  const MapZoomHandler: React.FC = () => {
    const map = useMap();

    useEffect(() => {
      const handleZoom = () => {
        const zoomLevel = map.getZoom();

        if (zoomLevel >= 8 && !showPrice) {
          setShowPrice(true);
          setIconSize([100, 20]);
        } else if (zoomLevel < 8 && showPrice) {
          setShowPrice(false);
          setIconSize([20, 20]);
        }
      };
      map.on('zoomend', handleZoom);

      handleZoom();
      return () => {
        map.off('zoomend', handleZoom);
      };
    }, [map]);

    return null;
  };

  if (!userPosition) {
    return <div>Loading map...</div>; 
  }

  const getConvertedPrice = (property: PropertyListing): { price: number, currency: string } => {
    let convertedPrice = property.price;
    let displayCurrency = property.currency;
  
    if (selectedCurrency === 'MIXTO') {
      return { price: convertedPrice, currency: displayCurrency };
    }
  
    if (selectedCurrency === 'CRC') {
      if (property.currency === 'USD' && buyRate) {
        convertedPrice = property.price * buyRate;
        displayCurrency = 'CRC';
      }
    }
  
    if (selectedCurrency === 'USD') {
      if (property.currency === 'CRC' && sellRate) {
        convertedPrice = property.price / sellRate;
        displayCurrency = 'USD';
      }
    }
  
    return { price: convertedPrice, currency: displayCurrency };
  };
  

  return (
    <Box sx={{ height: '80vh', width: '100%' }}>
      <MapContainer center={userPosition} zoom={12} style={{ height: '100%', width: '100%' }}>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        <MapZoomHandler />
        <Marker
          position={userPosition}
          icon={
            new L.DivIcon({
              className: 'user-location-icon',
              html: `
                <div class="user-location-wrapper">
                  <img src="/assets/images/alien.png" alt="Alien user icon" />
                </div>
              `,
              iconSize: [14, 14],
              popupAnchor: [0, -10],
            })
          }
        >
          <Popup>Tu ubicación actual</Popup>
        </Marker>


        {obscuredProperties.map((property) => {
          const { price: convertedPrice, currency: displayCurrency } = getConvertedPrice(property);

          return (
            <Marker
              key={property.id_Property}
              position={[parseFloat(property.latitude), parseFloat(property.longitude)]}
              icon={createPriceIcon(convertedPrice, showPrice, iconSize, displayCurrency)}
            >
              <Popup>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <img
                    src="/assets/images/logo-alien1-v3.png"
                    alt="Logo"
                    style={{ position: 'absolute', top: '0px', left: '0px', width: '60px', height: 'auto', zIndex: 1 }}
                  />
                  <PropertyCard
                    property={property}
                    isMapView={true}
                    onConvertPrice={onOpenModal}
                  />
                </div>
              </Popup>
            </Marker>
          );
        })}


      </MapContainer>
    </Box>
  );
};

export default MapView;
